/**
 * Sync Service - 오프라인 데이터 동기화
 *
 * 온라인 복귀 시 대기 중인 운동 기록을 서버로 전송
 * Background Sync API 활용 (지원 브라우저) + Fallback
 */

import {
  getPendingWorkouts,
  getWorkoutsToSync,
  updateWorkoutSyncStatus,
  removePendingWorkout,
  type PendingWorkout,
} from './indexedDB';
import { createIntegratedWorkout } from '../api/workout.api';

// 동기화 상태
export type SyncState = 'idle' | 'syncing' | 'success' | 'error';

// 동기화 이벤트 타입
export interface SyncEvent {
  type: 'start' | 'progress' | 'complete' | 'error';
  total: number;
  completed: number;
  failed: number;
  currentWorkout?: PendingWorkout;
  error?: string;
}

// 이벤트 리스너 타입
type SyncEventListener = (event: SyncEvent) => void;

// 싱글톤 인스턴스
let isInitialized = false;
const listeners: Set<SyncEventListener> = new Set();

/**
 * 동기화 이벤트 리스너 추가
 */
export function addSyncListener(listener: SyncEventListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * 동기화 이벤트 발송
 */
function emitSyncEvent(event: SyncEvent): void {
  listeners.forEach((listener) => listener(event));
}

/**
 * 단일 운동 기록 동기화
 */
async function syncSingleWorkout(workout: PendingWorkout): Promise<boolean> {
  try {
    await updateWorkoutSyncStatus(workout.id, 'syncing');

    await createIntegratedWorkout({
      exerciseId: workout.exerciseId,
      sets: workout.sets,
      reps: workout.reps,
      weight: workout.weight,
      workoutDate: workout.workoutDate,
      notes: workout.notes,
    });

    // 성공 시 삭제
    await removePendingWorkout(workout.id);
    return true;
  } catch (error: any) {
    console.error('Sync failed for workout:', workout.id, error);

    // 재시도 횟수 초과 시 실패 처리
    if (workout.retryCount >= 3) {
      await updateWorkoutSyncStatus(workout.id, 'failed', error.message);
    } else {
      await updateWorkoutSyncStatus(workout.id, 'pending', error.message);
    }

    return false;
  }
}

/**
 * 모든 대기 중인 운동 기록 동기화
 */
export async function syncAllPendingWorkouts(): Promise<{
  total: number;
  synced: number;
  failed: number;
}> {
  const workouts = await getWorkoutsToSync();

  if (workouts.length === 0) {
    return { total: 0, synced: 0, failed: 0 };
  }

  emitSyncEvent({
    type: 'start',
    total: workouts.length,
    completed: 0,
    failed: 0,
  });

  let synced = 0;
  let failed = 0;

  for (const workout of workouts) {
    emitSyncEvent({
      type: 'progress',
      total: workouts.length,
      completed: synced + failed,
      failed,
      currentWorkout: workout,
    });

    const success = await syncSingleWorkout(workout);
    if (success) {
      synced++;
    } else {
      failed++;
    }
  }

  emitSyncEvent({
    type: 'complete',
    total: workouts.length,
    completed: synced,
    failed,
  });

  return { total: workouts.length, synced, failed };
}

/**
 * 실패한 운동 기록 재시도
 */
export async function retryFailedWorkouts(): Promise<void> {
  const allWorkouts = await getPendingWorkouts();
  const failedWorkouts = allWorkouts.filter(
    (w) => w.syncStatus === 'failed' && w.retryCount < 5
  );

  for (const workout of failedWorkouts) {
    // 재시도 전 상태를 pending으로 변경
    await updateWorkoutSyncStatus(workout.id, 'pending');
  }

  // 동기화 실행
  await syncAllPendingWorkouts();
}

/**
 * 온라인 상태 변경 감지 및 자동 동기화
 */
export function initSyncService(): void {
  if (isInitialized) return;
  isInitialized = true;

  // 온라인 상태 변경 시 동기화
  const handleOnline = async () => {
    try {
      await syncAllPendingWorkouts();
    } catch (error) {
      console.error('Sync failed:', error);
    }
  };

  window.addEventListener('online', handleOnline);

  // 앱 시작 시 온라인이면 동기화
  if (navigator.onLine) {
    // 약간의 딜레이 후 동기화 (앱 로딩 완료 후)
    setTimeout(handleOnline, 2000);
  }

  // Background Sync API 등록 (지원 브라우저)
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready.then((registration) => {
      // Background sync 등록
      (registration as any).sync?.register('sync-workouts').catch((_err: Error) => {
        // Background sync registration failed silently
      });
    });
  }

  // Visibility change 이벤트 (앱이 다시 포커스될 때 동기화)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      handleOnline();
    }
  });
}

/**
 * 대기 중인 운동 기록 수 조회
 */
export async function getPendingCount(): Promise<number> {
  const workouts = await getWorkoutsToSync();
  return workouts.length;
}

/**
 * 동기화 가능 여부 확인
 */
export function canSync(): boolean {
  return navigator.onLine;
}

export default {
  initSyncService,
  syncAllPendingWorkouts,
  retryFailedWorkouts,
  addSyncListener,
  getPendingCount,
  canSync,
};
